import type { Mutex } from 'async-mutex';
// @ts-ignore
import type * as RCL from 'rclnodejs';

import { info, namespace } from '@sb/log';
import { getNode } from '@sb/ros/getNode';
import { timeout } from '@sb/utilities';

const ns = namespace('BaseService');

/**
 * Base service class for sending goals to ROS2 service servers.
 *
 * To use, extend this class, then override the following methods:
 * - `getType()`: Return the type of the Service as a string.
 * - `getServiceName()`: Return the name of the Service as a string.
 * - `createRequest()`: Generate a request to call to the service.
 */
export abstract class BaseService<
  ServiceType extends RCL.TypeClass<RCL.ServiceTypeClassName>,
  RequestType extends RCL.ServiceRequestMessage<ServiceType>,
  ResponseType extends RCL.ServiceResponseMessage<ServiceType>,
> {
  // Time to wait for the service to be available on initial client creation
  // We want to wait a bit longer on initial client creation to account
  // for startup times
  public static CLIENT_CREATION_TIMEOUT_MS = 3000;

  // Time to wait for the existing client to become available
  public static WAIT_FOR_SERVER_TIMEOUT_MS = 100;

  // Cache of client promises, (this prevents us from creating multiple clients for the same service)
  private static clientPromiseMap: Map<string, Promise<any>> = new Map();

  // remove the Service client from the cache, used mainly for testing
  public static deleteClient(className: string) {
    BaseService.clientPromiseMap.delete(className);
  }

  // Optional mutex that subclasses can provide to ensure exclusive access
  protected mutex?: Mutex;

  public async call(): Promise<ResponseType> {
    const client = await this.getClient();

    if (
      !(await client.waitForService(BaseService.WAIT_FOR_SERVER_TIMEOUT_MS))
    ) {
      throw new Error(
        `${this.getServiceName()} service not available after waiting ${BaseService.WAIT_FOR_SERVER_TIMEOUT_MS}ms`,
      );
    }

    const request: RequestType = await this.createRequest();

    const sendRequest = async (): Promise<ResponseType> => {
      // Check service availability again before sending because if we're running
      // exclusively, the service may have been stopped while waiting for the mutex
      if (!client.isServiceServerAvailable()) {
        throw new Error(`${this.getServiceName()} service not available`);
      }

      return new Promise<ResponseType>((resolve, reject) => {
        info(ns`call`, `sending request to ${this.getServiceName()} service`);

        client.sendRequest(
          request,
          (response: RCL.ServiceResponseMessage<ServiceType>) => {
            if (response) {
              resolve(response as ResponseType);
            } else {
              reject(
                new Error(
                  `Failed to get a response from ${this.getServiceName()} server`,
                ),
              );
            }
          },
        );
      });
    };

    const sendRequestWithTimeout = async (): Promise<ResponseType> => {
      const timeoutHandler = timeout(
        this.getTimeout(),
        `Request to ${this.getServiceName()} timed out after ${this.getTimeout()}ms`,
      );

      return timeoutHandler.race(sendRequest());
    };

    // If a mutex is provided, run the call exclusively
    if (this.mutex) {
      return this.mutex.runExclusive(sendRequestWithTimeout);
    }

    // Otherwise, just execute the call directly
    return sendRequestWithTimeout();
  }

  /**
   * Generate a request to call to the service.
   */
  protected createRequest(): Promise<RequestType> {
    throw new Error('Method not implemented.');
  }

  protected async getClient(): Promise<RCL.Client<ServiceType>> {
    let promise = BaseService.clientPromiseMap.get(this.constructor.name);

    if (!promise) {
      // Kick off creation immediately and store the Promise
      promise = (async () => {
        info(
          ns`getClient`,
          `creating client for ${this.getServiceName()} service`,
        );

        const client = (await getNode()).createClient(
          this.getType(),
          this.getServiceName(),
        );

        info(ns`getClient`, `waiting for ${this.getServiceName()} service...`);
        await client.waitForService(BaseService.CLIENT_CREATION_TIMEOUT_MS);

        return client;
      })();

      BaseService.clientPromiseMap.set(this.constructor.name, promise);
    }

    return promise;
  }

  /**
   * Get the ros2 Service type as a string
   * example: 'standard_bots_msgs/srv/IKCollisionCheck'
   */
  protected abstract getType(): RCL.TypeClass<keyof RCL.ServicesMap>;

  /**
   * Get the ros2 Service name as a string
   * example: 'solve_ik'
   */
  protected abstract getServiceName(): string;

  /**
   * Get the timeout for the service request in milliseconds.
   * Override this method to customize the timeout.
   */
  protected getTimeout(): number {
    return 3000;
  }
}
